import uuidv4 from 'uuid/v4'

const ALERT_TYPES = {
	info: true,
	success: true,
	warning: true,
	error: true,
}

export default class NotificationsHelper {
	/**
	 * @constructor
	 * @param {object} store - Vuex store
	 */
	constructor(store, {
		maxAlerts = 3,
		msAlerts = 5000,
		maxSnackbars = 1,
		msSnackbars = 2000,
	} = {}) {
		this._store = store
		this._uuid = uuidv4

		this._config = {
			alerts: {
				max: maxAlerts,
				ms: msAlerts,
			},
			snackbars: {
				max: maxSnackbars,
				ms: msSnackbars,
			},
		}
	}

	/**
	 * Return alerts
	 * @return {array} - Alerts
	 */
	getAlerts() {
		return this._store.getters['notifications/alerts']
	}

	/**
	 * Return snackbars
	 * @return {array} - Alerts
	 */
	getSnackbars() {
		return this._store.getters['notifications/snackbars']
	}

	/**
	 * Remove alert by array index
	 * @param {number} index - Array index
	 * @param {boolean} clear - set to true to clear alert timeout
	 */
	removeAlertIndex(index, clear = true) {
		const alert = this.getAlerts()[index]
		if (!alert) { return }
		if (clear && alert.timeout) {
			clearTimeout(alert.timeout)
		}

		this._store.dispatch('notifications/removeAlert', index)
	}

	/**
	 * Remove alert
	 * @param {string} id - Alert ID
	 * @param {boolean} clear - set to true to clear alert timeout
	 */
	removeAlert(id, clear = true) {
		const index = this.getAlerts().findIndex(alert => alert.id === id)
		if (index === -1) { return }

		this.removeAlertIndex(index, clear)
	}

	/**
	 * Push new alert notification
	 * @param {string} type - Notification type
	 * @param {string} message - Notification message
	 */
	pushAlert(type, message) {
		if (!ALERT_TYPES[type]) {
			console.warn(`Invalid alert type ${type} when pushing new alert`)
			return
		}

		const id = this._uuid()
		const notification = {
			id,
			type,
			message,
			timeout: setTimeout(() => this.removeAlert(id, false), this._config.alerts.ms),
		}

		this._store.dispatch('notifications/pushAlert', notification)
		const alerts = this.getAlerts()
		if (alerts.length > this._config.alerts.max) {
			this.removeAlertIndex(0)
		}
	}

	/**
	 * Remove snackbar by array index
	 * @param {number} index - Array index
	 * @param {boolean} clear - set to true to clear snackbar timeout
	 */
	removeSnackbarIndex(index, clear = true) {
		const alert = this.getSnackbars()[index]
		if (!alert) { return }
		if (clear && alert.timeout) {
			clearTimeout(alert.timeout)
		}

		this._store.dispatch('notifications/removeSnackbar', index)
	}

	/**
	 * Remove snackbar
	 * @param {string} id - Snackbar ID
	 * @param {boolean} clear - set to true to clear snackbar timeout
	 */
	removeSnackbar(id, clear = true) {
		const index = this.getSnackbars().findIndex(snackbar => snackbar.id === id)
		if (index === -1) { return }

		this.removeSnackbarIndex(index, clear)
	}

	/**
	 * Push new snackbar notification
	 * @param {string} message - Snackbar message
	 */
	pushSnackbar(message) {
		const id = this._uuid()
		const notification = {
			id,
			message,
			timeout: setTimeout(() => this.removeSnackbar(id, false), this._config.snackbars.ms),
		}

		this._store.dispatch('notifications/pushSnackbar', notification)
		const snackbars = this.getSnackbars()
		if (snackbars.length > this._config.snackbars.max) {
			this.removeSnackbarIndex(0)
		}
	}
}
